import { useEffect, useState, useCallback } from "react";
import { CloudOff, RefreshCw, Loader2 } from "lucide-react";
import { listQueue } from "../lib/offline-db";
import { onSyncStatus, flushQueue } from "../lib/sync-queue";

/**
 * PendingSyncBadge — floating pill that surfaces queued field-visit
 * writes (drafts, signatures, submits) waiting to reach the server.
 *
 * Polls `listQueue()` every 5 seconds while there are pending items,
 * and re-renders live when the sync queue emits status events. Shows:
 *   - Idle (no queue)             → null
 *   - Pending (queue > 0, idle)   → amber "1 visit pending sync — Tap to retry"
 *   - Syncing                     → blue "Syncing 1 of 2…"
 *   - Failed (3+ attempts)        → red "Submission failed — Tap to retry"
 *
 * Tap behaviour triggers `flushQueue({ source: "manual" })`.
 */
export default function PendingSyncBadge() {
  const [queueSize, setQueueSize] = useState(0);
  const [failed, setFailed] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ done: 0, total: 0 });

  const refresh = useCallback(async () => {
    const q = await listQueue();
    setQueueSize(q.length);
    setFailed(q.filter((e) => (e.attempts || 0) >= 3).length);
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 5000);
    const off = onSyncStatus((s) => {
      if (s.phase === "start") {
        setSyncing(true);
        setSyncProgress({ done: 0, total: s.total });
      } else if (s.phase === "step") {
        setSyncProgress({ done: s.processed, total: s.total });
      } else if (s.phase === "done") {
        setSyncing(false);
        refresh();
      }
    });
    return () => { clearInterval(interval); off(); };
  }, [refresh]);

  if (queueSize === 0 && !syncing) return null;

  let label, tone, Icon;
  if (syncing) {
    label = `Syncing ${syncProgress.done + 1} of ${syncProgress.total}…`;
    tone = "bg-blue-100 text-blue-800 border-blue-300";
    Icon = Loader2;
  } else if (failed > 0) {
    label = `Submission failed — tap to retry (${failed})`;
    tone = "bg-rose-100 text-rose-800 border-rose-300";
    Icon = CloudOff;
  } else {
    label = `${queueSize} visit${queueSize > 1 ? "s" : ""} pending sync — tap to retry`;
    tone = "bg-amber-100 text-amber-800 border-amber-300";
    Icon = RefreshCw;
  }

  return (
    <button
      type="button"
      onClick={() => flushQueue({ source: "manual" })}
      disabled={syncing}
      className={`fixed bottom-4 right-4 z-40 px-3.5 py-2 rounded-full border shadow-lg
                  text-[11px] font-bold flex items-center gap-2 transition
                  ${tone} disabled:cursor-not-allowed`}
      data-testid="pending-sync-badge"
    >
      <Icon className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
      {label}
    </button>
  );
}
